/**
 Copyright Highway9 Networks Inc.
 */
import { createSlice, PayloadAction, createAsyncThunk, createEntityAdapter, createSelector, type EntityState } from "@reduxjs/toolkit";
import { MonitoringSystem } from "~/types/monitoringSystem";
import { RootState } from "~/store";
import { networkService } from "~/services/network-service";
import { AaaServerProfile } from "~/types/AAAProfile";
import { createSocketPatchReducer } from "../events/socketPatch";

export const monitoringProfilesAdapter = createEntityAdapter<MonitoringSystem>();
export const aaaProfilesAdapter = createEntityAdapter<AaaServerProfile>();

interface MonitoringProfileState {
  open: boolean;
  selectedId: string | null;
  profilesData: EntityState<MonitoringSystem>;
  aaa: {
    open: boolean;
    selectedId: string | null;
    profilesData: EntityState<AaaServerProfile>;
  };
}

const initialState: MonitoringProfileState = {
  open: false,
  selectedId: null,
  profilesData: monitoringProfilesAdapter.getInitialState(),
  aaa: {
    open: false,
    selectedId: null,
    profilesData: aaaProfilesAdapter.getInitialState(),
  },
};

export const fetchMonitoringProfiles = createAsyncThunk("monitoringProfile/fetchMonitoringProfiles", async () => {
  const data = await networkService.getSNMPData();
  return data;
});

export const fetchAAAProfiles = createAsyncThunk("monitoringProfile/fetchAAAProfiles", async () => {
  const data = await networkService.getAAAServerProfilesData();
  return data;
});

const monitoringProfileSlice = createSlice({
  name: "monitoringProfile",
  initialState,
  reducers: {
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    setSelected: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
    },
    setAAASelected: (state, action: PayloadAction<string | null>) => {
      state.aaa.selectedId = action.payload;
    },
    setAAAOpen: (state, action: PayloadAction<boolean>) => {
      state.aaa.open = action.payload;
    },
    applySocketPatchesMonitoring: createSocketPatchReducer(monitoringProfilesAdapter, (s) => s.profilesData),
    applySocketPatchesAaa: createSocketPatchReducer(aaaProfilesAdapter, (s) => s.aaa.profilesData),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMonitoringProfiles.fulfilled, (state, action) => {
        monitoringProfilesAdapter.setAll(state.profilesData, action.payload);
      })
      .addCase(fetchAAAProfiles.fulfilled, (state, action) => {
        aaaProfilesAdapter.setAll(state.aaa.profilesData, action.payload);
      });
  },
});

export const monitoringProfileActions = monitoringProfileSlice.actions;
export default monitoringProfileSlice.reducer;

const monitoringProfileEntitySelectors = monitoringProfilesAdapter.getSelectors(
  (state: RootState) => state.monitoringProfile.profilesData,
);
const aaaProfileEntitySelectors = aaaProfilesAdapter.getSelectors(
  (state: RootState) => state.monitoringProfile.aaa.profilesData,
);

export const monitoringProfileOpen = (state: RootState) => state.monitoringProfile.open;

export const monitoringProfileData = monitoringProfileEntitySelectors.selectAll;
export const monitoringProfileEntities = monitoringProfileEntitySelectors.selectEntities;

export const monitoringProfileCurrent = createSelector(
  [(state: RootState) => state.monitoringProfile.selectedId, monitoringProfileEntities],
  (selectedId, entities): MonitoringSystem | null => (selectedId ? entities[selectedId] ?? null : null),
);

export const aaaProfileOpen = (state: RootState) => state.monitoringProfile.aaa.open;

export const aaaProfileData = aaaProfileEntitySelectors.selectAll;
export const aaaProfileEntities = aaaProfileEntitySelectors.selectEntities;

export const aaaProfileCurrent = createSelector(
  [(state: RootState) => state.monitoringProfile.aaa.selectedId, aaaProfileEntities],
  (selectedId, entities): AaaServerProfile | null => (selectedId ? entities[selectedId] ?? null : null),
);
